import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CategoryService } from '@admin-core/services/category.service';
import { SpecificationDefinitionService } from '@admin-core/services/specification-definition.service';
import { Category } from '@admin-core/models/category.model';
import { SpecificationDefinition } from '@admin-core/models/product.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-category-specs-admin',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: './category-specs-admin.component.html',
  styleUrl: './category-specs-admin.component.scss'
})
export class CategorySpecsAdminComponent implements OnInit {
  private readonly categoriesApi = inject(CategoryService);
  private readonly specsApi = inject(SpecificationDefinitionService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly leafCategories = signal<{ id: string; name: string; parentName: string }[]>([]);
  readonly tags = signal<SpecificationDefinition[]>([]);
  readonly selectedCategoryId = signal<string | null>(null);
  readonly loading = signal(false);
  readonly editingId = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(150)]]
  });

  ngOnInit(): void {
    this.categoriesApi.getTree().subscribe({
      next: (tree) => {
        const leaves: { id: string; name: string; parentName: string }[] = [];
        for (const parent of tree) {
          for (const child of parent.children ?? []) {
            leaves.push({ id: child.id, name: child.name, parentName: parent.name });
          }
        }
        this.leafCategories.set(leaves);
        if (leaves[0]) {
          this.selectCategory(leaves[0].id);
        }
      },
      error: () => this.showError(this.i18n.t('specs.loadCategoriesError'))
    });
  }

  selectCategory(categoryId: string): void {
    this.selectedCategoryId.set(categoryId);
    this.editingId.set(null);
    this.form.reset({ name: '' });
    this.loadTags(categoryId);
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form.reset({ name: '' });
  }

  openEdit(tag: SpecificationDefinition): void {
    this.editingId.set(tag.id);
    this.form.setValue({ name: tag.name });
  }

  moveTag(index: number, direction: -1 | 1): void {
    const items = [...this.tags()];
    const target = index + direction;
    if (target < 0 || target >= items.length) {
      return;
    }
    [items[index], items[target]] = [items[target], items[index]];
    this.tags.set(items);
  }

  saveOrder(): void {
    const categoryId = this.selectedCategoryId();
    if (!categoryId) {
      return;
    }

    this.specsApi.setOrder(categoryId, this.tags().map((t) => t.id)).subscribe({
      next: () => this.loadTags(categoryId),
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('specs.orderError'))
    });
  }

  save(): void {
    const categoryId = this.selectedCategoryId();
    if (!categoryId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const name = this.form.getRawValue().name.trim();
    const editingId = this.editingId();
    const req$ = editingId
      ? this.specsApi.update(categoryId, editingId, name)
      : this.specsApi.create(categoryId, name);

    req$.subscribe({
      next: () => {
        this.editingId.set(null);
        this.form.reset({ name: '' });
        this.loadTags(categoryId);
      },
      error: (err) =>
        this.showError(err?.error?.error ?? this.i18n.t('specs.saveError'))
    });
  }

  async remove(tag: SpecificationDefinition): Promise<void> {
    const categoryId = this.selectedCategoryId();
    if (!categoryId) {
      return;
    }
    const ok = await this.confirmDialog.confirm({
      title: this.i18n.t('common.confirmTitle'),
      message: this.i18n.t('specs.deleteConfirm', { name: tag.name }),
      confirmLabel: this.i18n.t('common.delete'),
      danger: true
    });
    if (!ok) {
      return;
    }

    this.specsApi.delete(categoryId, tag.id).subscribe({
      next: () => this.loadTags(categoryId),
      error: (err) =>
        this.showError(err?.error?.error ?? this.i18n.t('specs.deleteError'))
    });
  }

  private loadTags(categoryId: string): void {
    this.loading.set(true);
    this.specsApi.getByCategory(categoryId).subscribe({
      next: (tags) => {
        this.tags.set(tags);
        this.loading.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('specs.loadError'));
        this.loading.set(false);
      }
    });
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
